import { TAppDispatch, TRootState } from 'store/types'
import { captureException } from '@sentry/react'
import { ErrorKeys } from 'consts/errorKeys'
import { ApiError, ReduxThunkError, isDraftLimitError } from 'utils/errors'
import { handleApiError } from 'store/utils'
import { resizeImage_API } from 'api'
import slice from 'store/features/aiAvatar/slice'
import { aiAvatarSelectors } from 'store/features/aiAvatar/selectors'
import { withAppThunks } from 'store/with-app-thunks'
import {
  IResizeImageRequestBody,
  IResizeImageResponse
} from 'store/types/aiAvatar'
import {
  removePendingSourceImageEntry,
  saveAssetPendingOp,
  savePendingSourceImageEntry
} from 'store/features/aiAvatar/sourceImageStorage'
import { resolveTargetAssetId } from 'store/features/aiAvatar/thunks/sourceImage'
import { getSourceImageProgressId } from 'store/features/aiAvatar/utils'
import { AVATAR_SOURCE_IMAGE_EXPECTED_DURATION_SECONDS } from 'store/features/aiAvatar/consts'
import { generationProgressService } from 'services/generationProgress'

const { actions } = slice

/**
 * "Resize with AI" for the AI Avatar source image.
 *
 * Kicks off an AI reframe (outpaint/extend) of the currently selected avatar
 * image to `targetAspectRatio`, and shows the preview loader until the result
 * arrives. Works for every source type (upload / My Files / public).
 *
 * ASYNC DELIVERY MODEL
 * --------------------
 * `resizeImage_API` (recommendation-api `POST /gen-ai/resize-image`) returns
 * FAST; the resized image is NOT in the response. The response is
 * `{ imageId, projectId, resized }`:
 *   - `resized: true`  → generation started; `imageId` is the correlation key,
 *                        stashed as `pendingSourceImageId`.
 *   - `resized: false` → the image already matched the target ratio; nothing was
 *                        generated or charged (`imageId` is null) — release the
 *                        loader and stop.
 * The finished image arrives LATER on the shared `type:'ai-image'` socket
 * (`{ imageId, filePath, width, height, error }`), correlated PURELY by
 * `imageId`. `handleAIImgSocketData` (src/services/socket/aiImage.ts) matches
 * `pendingSourceImageId`, applies `updateAvatar` + `setAvatarAspectRatio`
 * (from `pendingSourceAspectRatio`), and clears the loader/correlation.
 *
 * LINCHPIN (verify in-app): the `type:'ai-image'` socket must reach this user
 * for reco-TRIGGERED generations, not only front-triggered ones. If it doesn't,
 * the loader hangs (see the reconnect-replay note in the socket handler).
 *
 * USAGE
 * -----
 *   dispatch(aiAvatarThunks.resizeAvatarImage('9:16'))
 */
const resizeAvatarImage = (targetAspectRatio: string) =>
  withAppThunks(
    appThunks =>
      async (
        dispatch: TAppDispatch,
        getState: () => TRootState
      ): Promise<IResizeImageResponse | undefined> => {
        const avatar = aiAvatarSelectors.avatar(getState())
        if (!avatar?.url) return

        // Loader over the current preview starts immediately; the socket branch
        // clears it on completion (no `finally` — that would drop it while the
        // socket is still pending).
        dispatch(actions.setAvatarLoading(true))
        // Set once the progress ticker starts, so the catch can unwind it.
        let startedImageId: number | null = null
        try {
          const projectId = await dispatch(
            appThunks.projectThunks.getOrCreateProjectId()
          )

          const body: IResizeImageRequestBody = {
            imageUrl: avatar.url,
            aspectRatio: targetAspectRatio,
            videoProjectId: projectId
          }

          const { data } = await resizeImage_API(body)

          // Already this ratio → no generation, no charge, no socket event.
          if (!data.resized || data.imageId === null) {
            dispatch(actions.setAvatarLoading(false))
            return data
          }

          // Resize CHANGES the ratio → commit `targetAspectRatio` on completion.
          dispatch(
            actions.setPendingSourceImage({
              id: data.imageId,
              aspectRatio: targetAspectRatio
            })
          )
          // Simulated percentage for the preview's progress ring (persisted, so
          // it resumes after a reload); the socket/reconcile terminals
          // complete/stop it by the same namespaced id.
          generationProgressService.start(
            getSourceImageProgressId(data.imageId),
            String(data.imageId),
            AVATAR_SOURCE_IMAGE_EXPECTED_DURATION_SECONDS,
            false,
            0
          )
          startedImageId = data.imageId
          // Persist the op so it survives navigation/reloads (see
          // sourceImageStorage): a fresh form (case 1) uses the single-slot
          // entry; a regen form (case 2) uses the assetId-keyed ops map, so
          // deselecting the clip no longer abandons the paid op.
          if (aiAvatarSelectors.targetMediaItemId(getState()) === null) {
            savePendingSourceImageEntry({
              videoProjectId: Number(projectId),
              imageId: data.imageId,
              targetAspectRatio,
              avatar,
              ratioOverride: aiAvatarSelectors.avatarAspectRatio(getState()),
              createdAt: Date.now()
            })
          } else {
            const assetId = resolveTargetAssetId(getState())
            if (assetId) {
              saveAssetPendingOp(assetId, {
                imageId: data.imageId,
                targetAspectRatio,
                createdAt: Date.now()
              })
            }
          }
          return data
        } catch (error) {
          // Draft-limit 409: already surfaced by createProject with its own
          // upgrade popup — don't mask it with a generic error popup.
          if (!isDraftLimitError(error)) {
            if (!(error instanceof ApiError)) {
              captureException(new ReduxThunkError(error))
            }
            dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
          }
          // The socket won't fire on failure, so unwind loader + correlation
          // (and the persisted entry, when the failure hit after the write).
          dispatch(actions.setAvatarLoading(false))
          dispatch(actions.clearPendingSourceImage())
          removePendingSourceImageEntry()
          if (startedImageId !== null) {
            generationProgressService.stop({
              mediaItemId: getSourceImageProgressId(startedImageId),
              shouldRemoveFromStorage: true
            })
          }
        }
      }
  )

export const resizeThunks = { resizeAvatarImage }
